import { useEffect, useRef, useState } from 'react';

export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Japanese%20elderly%20care%20dayservice%20center%20warm%20sunlight%20interior%20staff%20helping%20senior%20residents%20cheerful%20bright%20clean%20facility%20tatami%20room%20flowers%20natural%20light%20soft%20tones%20peaceful%20atmosphere%20professional%20caregivers%20smiling%20elderly%20people%20activity%20room&width=1920&height=1080&seq=hero001&orientation=landscape"
          alt="さくらデイサービスの温かな日常風景"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-24 pb-16">
        <div className="max-w-2xl">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-1.5 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full mb-6 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="w-2 h-2 rounded-full bg-[#88B277]" />
            <span className="text-white text-xs font-medium tracking-widest">デイサービス・通所介護</span>
          </div>

          {/* Main Title */}
          <h1
            className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 transition-all duration-700 delay-150 ${
              visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-95'
            }`}
          >
            毎日が、
            <br />
            <span className="text-[#b8d9a8]">笑顔でいられる</span>
            <br />
            場所でありたい。
          </h1>

          {/* Subtitle */}
          <p
            className={`text-white/85 text-base md:text-lg leading-relaxed mb-10 max-w-lg transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            さくらデイサービスは、ご利用者おひとりおひとりの生活リズムを大切にしながら、
            安心して過ごせる時間をご提供しています。
          </p>

          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <a
              href="tel:0120-000-000"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#88B277] text-white font-semibold rounded-full hover:bg-[#76a065] transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.41 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.82a16 16 0 0 0 6.29 6.29l.98-.98a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              電話で相談する
            </a>
            <button
              onClick={() => handleScroll('#services')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/15 backdrop-blur-sm border border-white/40 text-white font-medium rounded-full hover:bg-white/25 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              サービスを見る
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        <div
          className={`mt-16 md:mt-24 grid grid-cols-3 gap-4 max-w-lg transition-all duration-700 delay-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {[
            { value: '創業15年', label: '地域に根ざした実績' },
            { value: '定員30名', label: '少人数制のきめ細かなケア' },
            { value: '介護士12名', label: '専門資格を持つスタッフ' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-white font-bold text-lg md:text-xl">{stat.value}</div>
              <div className="text-white/70 text-xs mt-1 leading-tight">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 transition-all duration-700 delay-1000 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <span className="text-white/60 text-xs tracking-widest">SCROLL</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/60 to-transparent animate-pulse" />
      </div>
    </section>
  );
}
